import { createContext, useContext, useReducer, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { collection } from '@app/firebase/firestore'
import { signUp } from '@app/firebase/auth'
import { createOrderNetwork } from '@app/firebase/functions'

// Initial state for the reducer
const initialState = {
  disabled: false,
  network: {
    id: null,
    name: null,
    data: null,
  },
  service: {
    id: null,
    data: null,
  },
  error: null,
}

// Action types
const ACTION_TYPES = {
  SET_DISABLED: 'SET_DISABLED',
  SET_NETWORK: 'SET_NETWORK',
  SET_SERVICE_ID: 'SET_SERVICE_ID',
  SET_SERVICE: 'SET_SERVICE',
  SET_ERROR: 'SET_ERROR',
  RESET_NETWORK: 'RESET_NETWORK',
}

// Reducer function to handle state updates
function checkoutReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_DISABLED:
      return { ...state, disabled: action.payload }
    case ACTION_TYPES.SET_NETWORK:
      return {
        ...state,
        network: {
          ...state.network,
          ...action.payload,
        },
      }
    case ACTION_TYPES.SET_SERVICE_ID:
      return {
        ...state,
        service: {
          ...state.service,
          id: action.payload,
        },
      }
    case ACTION_TYPES.SET_SERVICE:
      return {
        ...state,
        service: {
          ...state.service,
          data: action.payload,
        },
      }
    case ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload }
    case ACTION_TYPES.RESET_NETWORK:
      return {
        ...state,
        network: initialState.network,
      }
    default:
      return state
  }
}

const QuickCheckoutNetwork = createContext()

export const useQuickCheckout = () => useContext(QuickCheckoutNetwork)

const QuickCheckoutNetworkProvider = ({ paymentIntent, children }) => {
  const paymentIntentId = paymentIntent || null

  const [state, dispatch] = useReducer(checkoutReducer, initialState)
  const { disabled, network, service, error } = state

  const setError = useCallback((err) => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: err })
  }, [])

  const setDisabled = useCallback((value) => {
    dispatch({ type: ACTION_TYPES.SET_DISABLED, payload: value })
  }, [])

  const setNetworkId = useCallback((id) => {
    dispatch({ type: ACTION_TYPES.SET_NETWORK, payload: { id } })
  }, [])

  const setNetworkName = useCallback((name) => {
    dispatch({ type: ACTION_TYPES.SET_NETWORK, payload: { name } })
  }, [])

  const setServiceId = useCallback((id) => {
    dispatch({ type: ACTION_TYPES.SET_SERVICE_ID, payload: id })
  }, [])

  const createAccount = async (email, password, profile) => {
    try {
      const status = await signUp(email, password, profile)
      return {
        id: status?.uid,
        status: 'success',
      }
    } catch (error) {
      setError(error)
      return {
        code: error?.code,
        status: 'error',
      }
    }
  }

  const createOrder = async (formData) => {
    if (!service.data || !network.data || !paymentIntentId) {
      setError(new Error('Missing required data for order creation'))
      return false
    }

    const paymentInfo = {
      serviceId: service.id,
      networkId: network.id,
      networkName: network.name,
      paymentIntentId,
      ...formData,
    }

    try {
      const orderResponse = await createOrderNetwork(paymentInfo)
      if (!orderResponse) return false
      return orderResponse?.paymentIntentId
    } catch (err) {
      setError(err)
      return false
    }
  }

  const getService = useCallback(
    async (serId) => {
      if (!serId || !network.id) {
        dispatch({
          type: ACTION_TYPES.SET_SERVICE,
          payload: null,
        })
        return
      }

      try {
        const serviceDoc = await collection('networks')
          .doc(network.id)
          .collection('services')
          .doc(serId)
          .get()

        if (serviceDoc !== undefined && serviceDoc.exists) {
          const docData = serviceDoc.data()
          dispatch({
            type: ACTION_TYPES.SET_SERVICE,
            payload: { ...docData, id: serviceDoc.id },
          })
        } else {
          dispatch({
            type: ACTION_TYPES.SET_SERVICE,
            payload: null,
          })
        }
      } catch (err) {
        setError(err)
        dispatch({
          type: ACTION_TYPES.SET_SERVICE,
          payload: null,
        })
      }
    },
    [network.id, setError],
  )

  const getNetworkByName = useCallback(
    async (netName) => {
      if (!netName) return

      try {
        const networkDocs = await collection('networks').where('slug', '==', netName).get()

        if (networkDocs.empty) {
          dispatch({ type: ACTION_TYPES.RESET_NETWORK })
          return
        }

        const networkData = {
          id: networkDocs.docs[0].id,
          name: networkDocs.docs[0].data().name,
          data: {
            id: networkDocs.docs[0].id,
            ...networkDocs.docs[0].data(),
          },
        }

        dispatch({
          type: ACTION_TYPES.SET_NETWORK,
          payload: networkData,
        })
      } catch (err) {
        setError(err)
        dispatch({ type: ACTION_TYPES.RESET_NETWORK })
      }
    },
    [setError],
  )

  const getNetworkByID = useCallback(
    async (netId) => {
      if (!netId) return

      try {
        const networkDoc = await collection('networks').doc(netId).get()

        if (!networkDoc.exists) {
          dispatch({ type: ACTION_TYPES.RESET_NETWORK })
          return
        }

        const networkData = {
          id: networkDoc.id,
          name: networkDoc.data().name,
          data: {
            id: networkDoc.id,
            ...networkDoc.data(),
          },
        }

        dispatch({
          type: ACTION_TYPES.SET_NETWORK,
          payload: networkData,
        })
      } catch (err) {
        setError(err)
        dispatch({ type: ACTION_TYPES.RESET_NETWORK })
      }
    },
    [setError],
  )

  useEffect(() => {
    let mounted = true

    if (service.id && mounted) {
      getService(service.id)
    }

    return () => {
      mounted = false
    }
  }, [service.id, getService])

  const value = {
    networkId: network.id,
    setNetworkId,
    networkName: network.name,
    setNetworkName,
    serviceId: service.id,
    setServiceId,
    service: service.data,
    getNetworkByName,
    getNetworkByID,
    networkObj: network.data,
    createAccount,
    createOrder,
    firebaseError: error,
    disabled,
    setDisabled,
  }

  return <QuickCheckoutNetwork.Provider value={value}>{children}</QuickCheckoutNetwork.Provider>
}

QuickCheckoutNetworkProvider.propTypes = {
  children: PropTypes.node.isRequired,
  paymentIntent: PropTypes.string,
}

QuickCheckoutNetworkProvider.defaultProps = {
  paymentIntent: null,
}

export default QuickCheckoutNetworkProvider
